import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Coins, Receipt, Search, TrendingUp } from 'lucide-react';
import {
  Badge,
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
  PageSpinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { formatDate, formatNumber } from '@/lib/utils/format';
import { fetchClients } from '@/features/clients/api';
import { fetchBillingSummary, fetchInvoices, type BillingSummary, type Invoice } from './api';

export function BillingPage() {
  const [clientId, setClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  // Load client list for the selector
  const clientsQuery = useQuery({
    queryKey: ['dashboard', 'clients', clientSearch],
    queryFn: () =>
      fetchClients({
        searchTerm: clientSearch || undefined,
        clientStatus: 'ACTIVE',
        page: 0,
        size: 50,
      }),
  });

  const summaryQuery = useQuery({
    queryKey: ['billing', 'summary', clientId],
    queryFn: () => fetchBillingSummary(clientId || undefined),
    enabled: true, // always run — backend returns empty data when no clientId
  });

  const invoicesQuery = useQuery({
    queryKey: ['billing', 'invoices', clientId],
    queryFn: () => fetchInvoices(clientId || undefined),
    enabled: !!clientId, // only fetch invoices when a client is selected
  });

  const clients = clientsQuery.data?.content ?? [];
  const selectedClient = clients.find((c) => c.id === clientId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Billing & Invoices</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          View credit balance and usage for a specific client.
        </p>
      </div>

      {/* Client selector */}
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search"
              placeholder="Search active clients…"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="h-10 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="h-10 min-w-[240px] rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
          >
            <option value="">Select a client to view billing</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.clientName}
              </option>
            ))}
          </select>
        </div>
        {selectedClient && (
          <p className="mt-2 text-xs text-fg-muted">
            Showing billing for{' '}
            <span className="font-medium text-fg">{selectedClient.clientName}</span>
            {' · '}
            <span className="font-mono">{selectedClient.id}</span>
          </p>
        )}
      </Card>

      {/* Summary cards */}
      {summaryQuery.isLoading ? (
        <PageSpinner />
      ) : summaryQuery.isError ? (
        <EmptyState
          message={
            isApiError(summaryQuery.error)
              ? summaryQuery.error.firstErrorMessage
              : 'Could not load billing summary.'
          }
        />
      ) : (
        <>
          {!clientId && (
            <Card>
              <div className="py-8 text-center">
                <Receipt className="mx-auto mb-3 h-10 w-10 text-fg-subtle" />
                <p className="text-sm font-medium text-fg">Select a client above</p>
                <p className="mt-1 text-xs text-fg-muted">
                  Choose an active client to view their credit balance, usage, and invoices.
                </p>
              </div>
            </Card>
          )}

          {clientId && summaryQuery.data && (
            <SummaryCards summary={summaryQuery.data} />
          )}
        </>
      )}

      {/* Invoices */}
      {clientId && (
        <Card noPadding>
          <div className="flex items-center justify-between border-b border-border p-4 sm:p-6">
            <CardHeader className="m-0">
              <CardTitle>Invoices</CardTitle>
            </CardHeader>
          </div>
          <InvoicesTable
            isLoading={invoicesQuery.isLoading}
            isError={invoicesQuery.isError}
            rows={invoicesQuery.data ?? []}
          />
        </Card>
      )}
    </div>
  );
}

function SummaryCards({ summary }: { summary: BillingSummary }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Card>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-fg-muted">Remaining credits</div>
            <div className="mt-1 text-2xl font-semibold text-fg">
              {formatNumber(summary.balance)}
            </div>
            {summary.balanceExpiryDate && (
              <div className="mt-1 text-xs text-fg-muted">
                {summary.balanceValid ? 'Expires' : 'Expired'}{' '}
                {formatDate(summary.balanceExpiryDate)}
              </div>
            )}
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
            <Coins className="h-5 w-5" />
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-fg-muted">Used this month</div>
            <div className="mt-1 text-2xl font-semibold text-fg">
              {formatNumber(summary.usedThisMonth)}
            </div>
            <div className="mt-1 text-xs text-fg-muted">
              {formatNumber(summary.usedToday)} today ·{' '}
              avg {Math.round(summary.averageDailyUsage)}/day
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
            <TrendingUp className="h-5 w-5" />
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-fg-muted">Balance status</div>
            <div className="mt-2">
              <Badge tone={summary.balanceValid ? 'success' : 'danger'}>
                {summary.balanceValid ? 'ACTIVE' : 'EXPIRED'}
              </Badge>
            </div>
            <div className="mt-2 text-xs text-fg-muted">
              Balance is consumed per verification, regardless of result.
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
            <Receipt className="h-5 w-5" />
          </div>
        </div>
      </Card>
    </div>
  );
}

function InvoicesTable({
  isLoading,
  isError,
  rows,
}: {
  isLoading: boolean;
  isError: boolean;
  rows: Invoice[];
}) {
  if (isLoading) return <div className="py-12"><PageSpinner /></div>;
  if (isError) return <EmptyState message="Could not load invoices." />;
  if (rows.length === 0) {
    return <EmptyState message="No invoices on record for this client yet." />;
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Invoice</TH>
          <TH>Period</TH>
          <TH>Amount</TH>
          <TH>Status</TH>
          <TH className="text-right">Issued</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.id}>
            <TD className="font-mono text-xs">{row.id}</TD>
            <TD className="text-fg-muted">
              {formatDate(row.periodStart)} – {formatDate(row.periodEnd)}
            </TD>
            <TD>
              {(row.amountCents / 100).toFixed(2)} {row.currency}
            </TD>
            <TD>
              <Badge tone={row.status === 'PAID' ? 'success' : 'warning'}>{row.status}</Badge>
            </TD>
            <TD className="text-right text-fg-muted">{formatDate(row.issuedAt)}</TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
